/* Mega menu (desktop, 1100 px and wider).
   <lmb-mega-menu> holds one [data-mega-panel] per header menu item. A panel belongs to the
   header nav link (.header__nav-link[data-mega-key]) whose URL matches its data-mega-item.
   Product cards are fetched once, on the first pointer entry into the nav or focus on a nav link. */
(() => {
  if (customElements.get('lmb-mega-menu')) return;

  const DESKTOP = '(min-width: 1100px)';
  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 200;
  const POINTER_FOCUS_WINDOW = 800;
  const NAV = '.header__nav';
  const NAV_LINK = '.header__nav-link[data-mega-key]';

  /* Card HTML per section URL, shared across re-renders in the theme editor. */
  const cardCache = new Map();

  const loadCards = (url) => {
    if (!cardCache.has(url)) {
      const request = window.LMB.fetchHTML(url)
        .then((doc) => {
          const cards = doc.querySelector('[data-mega-cards]');
          return cards ? cards.innerHTML : null;
        })
        .catch(() => {
          cardCache.delete(url);
          return null;
        });
      cardCache.set(url, request);
    }
    return cardCache.get(url);
  };

  /* "/fr/collections/Rings/?b=2&a=1" → "/collections/rings?a=1&b=2" */
  const normalise = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      let path = url.pathname.replace(/\/+$/, '').toLowerCase() || '/';
      const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
        .replace(/\/+$/, '')
        .toLowerCase();
      if (root && path.startsWith(`${root}/`)) path = path.slice(root.length);
      const params = new URLSearchParams(url.search);
      params.sort();
      const search = params.toString();
      return search ? `${path}?${search}` : path;
    } catch (error) {
      return String(value).trim();
    }
  };

  const asElement = (node) => (node instanceof Element ? node : null);

  class LmbMegaMenu extends HTMLElement {
    connectedCallback() {
      this.mq = window.matchMedia(DESKTOP);
      this.hover = this.dataset.hover !== 'false';
      this.scrim = this.querySelector('[data-mega-scrim]');
      this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
      this.keys = new Map();
      this.panels.forEach((panel) => {
        const key = normalise(panel.dataset.megaItem);
        if (key && !this.keys.has(key)) this.keys.set(key, panel);
      });
      this.current = null;
      this.pinned = false;
      this.pointerType = 'mouse';
      this.pointerDownAt = -Infinity;
      this.links = [];

      this.listeners = [
        [document, 'pointerover', (event) => this.onPointerOver(event)],
        [document, 'pointerout', (event) => this.onPointerOut(event)],
        [document, 'pointerdown', (event) => this.onPointerDown(event)],
        [document, 'click', (event) => this.onClick(event)],
        [document, 'focusin', (event) => this.onFocusIn(event)],
        [document, 'keydown', (event) => this.onKeydown(event)],
        [document, 'lmb:overlay-open', () => this.close()],
        [document, 'shopify:section:load', () => this.refresh()],
        [document, 'shopify:section:select', (event) => this.onSectionSelect(event, true)],
        [document, 'shopify:section:deselect', (event) => this.onSectionSelect(event, false)],
        [this, 'shopify:block:select', (event) => this.onBlockSelect(event)],
        [this, 'shopify:block:deselect', () => this.close()],
        [this.mq, 'change', () => {
          if (!this.mq.matches) this.close();
        }],
        [window, 'pageshow', (event) => {
          if (event.persisted) this.close();
        }],
      ];
      this.listeners.forEach(([target, type, handler]) => target.addEventListener(type, handler));

      this.bindLinks();
    }

    disconnectedCallback() {
      if (this.listeners) {
        this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
      }
      this.close();
      this.unbindLinks();
    }

    /* ---------- Header links ---------- */

    bindLinks() {
      this.unbindLinks();
      this.links = Array.from(document.querySelectorAll(NAV_LINK));
      this.links.forEach((link) => {
        const panel = this.panelFor(link);
        if (!panel) return;
        link.setAttribute('aria-controls', panel.id);
        link.setAttribute('aria-expanded', 'false');
        link.dataset.megaBound = '';
      });
    }

    unbindLinks() {
      (this.links || []).forEach((link) => {
        if (!link.hasAttribute('data-mega-bound')) return;
        link.removeAttribute('aria-controls');
        link.removeAttribute('aria-expanded');
        link.removeAttribute('data-mega-bound');
        link.classList.remove('is-open');
      });
    }

    /* The header or this section was re-rendered in the theme editor. */
    refresh() {
      this.close();
      this.bindLinks();
    }

    panelFor(link) {
      return (link && this.keys.get(normalise(link.dataset.megaKey))) || null;
    }

    linkFor(panel) {
      return this.links.find((link) => document.contains(link) && this.panelFor(link) === panel) || null;
    }

    inZone(node) {
      const element = asElement(node);
      if (!element) return false;
      return Boolean(element.closest(NAV) || (this.contains(element) && element.closest('[data-mega-panel]')));
    }

    /* ---------- Open / close ---------- */

    open(panel, link) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!panel || !this.mq.matches) return;
      this.prefetch();

      const previous = this.current;
      if (previous && previous.panel === panel && previous.link === link) return;
      if (previous) {
        previous.panel.hidden = true;
        this.setLinkState(previous.link, false);
      }

      // Fade in only when the menu was closed; switching panels is instant.
      panel.classList.toggle('is-entering', !previous);
      panel.hidden = false;
      if (this.scrim) this.scrim.hidden = false;
      this.classList.add('is-open');
      this.setLinkState(link, true);
      this.current = { panel, link };
    }

    close({ focus = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.pinned = false;
      if (!this.current) return;

      const { panel, link } = this.current;
      const focusInside = panel.contains(document.activeElement);
      this.current = null;
      panel.hidden = true;
      if (this.scrim) this.scrim.hidden = true;
      this.classList.remove('is-open');
      this.setLinkState(link, false);

      // Never leave focus on a hidden element.
      if ((focus || focusInside) && link && document.contains(link)) this.focusQuietly(link);
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      // In the theme editor a selected panel stays open while the pointer moves around.
      if (this.pinned) return;
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    setLinkState(link, open) {
      if (!link) return;
      link.classList.toggle('is-open', open);
      if (link.hasAttribute('aria-controls')) link.setAttribute('aria-expanded', String(open));
    }

    /* Moves focus without the focus handler reopening or closing a panel. */
    focusQuietly(element) {
      this.ignoreFocus = true;
      element.focus({ preventScroll: true });
      this.ignoreFocus = false;
    }

    /* ---------- Product cards ---------- */

    /* Loads every panel's cards once. A slot whose request failed keeps its placeholders
       and is tried again the next time the menu is reached. */
    prefetch() {
      if (this.dataset.cards === 'false') return;
      this.querySelectorAll('[data-mega-cards][data-url]:not([data-loading])').forEach((slot) => {
        slot.setAttribute('data-loading', '');
        loadCards(slot.dataset.url).then((html) => {
          if (!slot.isConnected) return;
          slot.removeAttribute('data-loading');
          if (html == null) return;
          slot.innerHTML = html;
          slot.removeAttribute('data-url');
        });
      });
    }

    /* ---------- Pointer ---------- */

    onPointerDown(event) {
      this.pointerType = event.pointerType || 'mouse';
      this.pointerDownAt = performance.now();
    }

    onPointerOver(event) {
      if (event.pointerType === 'touch' || !this.mq.matches) return;
      const target = asElement(event.target);
      if (!target) return;
      const nav = target.closest(NAV);
      if (nav) this.prefetch();
      if (!this.hover || !this.inZone(target)) return;

      clearTimeout(this.closeTimer);
      if (!nav) return; // Inside the open panel.

      const link = target.closest('.header__nav-link');
      if (!link) return;
      clearTimeout(this.openTimer);
      const panel = this.panelFor(link);
      if (!panel) {
        this.close();
        return;
      }
      if (this.current) {
        this.open(panel, link);
      } else {
        this.openTimer = setTimeout(() => this.open(panel, link), OPEN_DELAY);
      }
    }

    onPointerOut(event) {
      if (event.pointerType === 'touch' || !this.hover || !this.mq.matches) return;
      if (!this.inZone(event.target) || this.inZone(event.relatedTarget)) return;
      clearTimeout(this.openTimer);
      if (this.current) this.scheduleClose();
    }

    /* Touch, or "Open on hover" turned off: the first click opens the panel, the next one follows the link. */
    onClick(event) {
      if (!this.mq.matches) return;
      const target = asElement(event.target);
      if (!target) return;

      const link = target.closest(NAV_LINK);
      if (link && target.closest(NAV)) {
        const panel = this.panelFor(link);
        if (!panel) return;
        const clickToOpen = !this.hover || this.pointerType === 'touch';
        if (!clickToOpen) return;
        if (this.current && this.current.link === link) return;
        event.preventDefault();
        this.open(panel, link);
        return;
      }

      // A click anywhere outside the nav and the panel (the scrim included) closes the menu.
      if (this.current && !this.inZone(target)) this.close();
    }

    /* ---------- Keyboard ---------- */

    onFocusIn(event) {
      if (this.ignoreFocus || !this.mq.matches) return;
      const target = asElement(event.target);
      if (!target) return;
      // Focus that comes from a click or tap is handled by the pointer handlers.
      const fromPointer = performance.now() - this.pointerDownAt < POINTER_FOCUS_WINDOW;

      const link = target.closest(NAV_LINK);
      if (link && target.closest(NAV)) {
        this.prefetch();
        if (fromPointer) return;
        const panel = this.panelFor(link);
        if (panel) this.open(panel, link);
        else this.close();
        return;
      }

      if (!this.current) return;
      if (this.current.panel.contains(target)) {
        clearTimeout(this.closeTimer);
        return;
      }
      if (!fromPointer) this.close();
    }

    onKeydown(event) {
      // Focus that follows a key press is keyboard focus, even right after a click.
      this.pointerDownAt = -Infinity;
      if (!this.mq.matches || event.defaultPrevented) return;
      const target = asElement(event.target);

      if (event.key === 'Escape' || event.key === 'Esc') {
        if (!this.current) return;
        const active = document.activeElement;
        const focusInMenu = this.current.panel.contains(active) || active === this.current.link;
        this.close({ focus: focusInMenu });
        return;
      }
      if (!target) return;
      const arrow = (event.key === 'ArrowDown' || event.key === 'ArrowUp') && !event.altKey && !event.ctrlKey && !event.metaKey;

      // ArrowDown on a nav link moves into its panel.
      const navLink = target.closest(NAV_LINK);
      if (arrow && event.key === 'ArrowDown' && navLink && target.closest(NAV)) {
        const panel = this.panelFor(navLink);
        if (!panel) return;
        event.preventDefault();
        this.open(panel, navLink);
        const first = window.LMB.focusables(panel)[0];
        if (first) first.focus();
        return;
      }

      if (!this.current || !this.current.panel.contains(target)) return;
      const { panel, link: owner } = this.current;
      const items = window.LMB.focusables(panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const index = items.indexOf(target);

      if ((arrow && event.key === 'ArrowUp' && target === first) || (event.key === 'Tab' && event.shiftKey && target === first)) {
        // Back to the menu item; the panel stays open.
        if (!owner || !document.contains(owner)) return;
        event.preventDefault();
        this.focusQuietly(owner);
        return;
      }

      // Arrow keys step through the panel's links instead of scrolling the page behind it.
      if (arrow && index > -1) {
        event.preventDefault();
        const next = items[index + (event.key === 'ArrowDown' ? 1 : -1)];
        if (next) next.focus();
        return;
      }

      if (event.key === 'Tab' && !event.shiftKey && target === last) {
        // Tabbing past the last link continues with whatever follows the menu item in the header
        // (the next menu item opens its own panel; anything else closes this one).
        const next = this.focusableAfter(owner);
        if (next) {
          event.preventDefault();
          next.focus();
        } else {
          this.close();
        }
      }
    }

    focusableAfter(link) {
      if (!link || !document.contains(link)) return null;
      const scope = link.closest('lmb-header, header, .section-site-header') || document.body;
      const items = window.LMB.focusables(scope);
      const index = items.indexOf(link);
      return index > -1 ? items[index + 1] || null : null;
    }

    /* ---------- Theme editor ---------- */

    onBlockSelect(event) {
      const panel = asElement(event.target) && event.target.closest('[data-mega-panel]');
      if (!panel || !this.contains(panel)) return;
      this.open(panel, this.linkFor(panel));
      if (this.current) this.pinned = true;
    }

    onSectionSelect(event, selected) {
      const section = asElement(event.target);
      if (!section || !section.contains(this)) return;
      if (!selected) {
        this.close();
        return;
      }
      if (!this.current && this.panels.length) this.open(this.panels[0], this.linkFor(this.panels[0]));
    }
  }

  customElements.define('lmb-mega-menu', LmbMegaMenu);
})();
